// Offline storage utility for TeenVest game modes
// Stores progress locally when offline and syncs when back online

const STORAGE_KEYS = {
  NEWS_REACTOR_PROGRESS: 'teenvest_news_reactor_progress',
  LEARN_MODE_PROGRESS: 'teenvest_learn_mode_progress',
  CHAOS_MODE_SCORES: 'teenvest_chaos_mode_scores',
  PENDING_SYNC: 'teenvest_pending_sync',
  USER_DATA: 'teenvest_user_data',
};

// Save data to localStorage
export const saveOfflineData = (key, data) => {
  try {
    localStorage.setItem(key, JSON.stringify({
      data,
      timestamp: Date.now(),
      synced: navigator.onLine
    }));
    return true;
  } catch (error) {
    console.error('Failed to save offline data:', error);
    return false;
  }
};

// Get data from localStorage
export const getOfflineData = (key) => {
  try {
    const stored = localStorage.getItem(key);
    if (stored) {
      return JSON.parse(stored);
    }
    return null;
  } catch (error) {
    console.error('Failed to get offline data:', error);
    return null;
  }
};

// Save News Reactor progress
export const saveNewsReactorProgress = (progress) => {
  return saveOfflineData(STORAGE_KEYS.NEWS_REACTOR_PROGRESS, progress);
};

// Get News Reactor progress
export const getNewsReactorProgress = () => {
  const stored = getOfflineData(STORAGE_KEYS.NEWS_REACTOR_PROGRESS);
  return stored?.data || null;
};

// Save Learn Mode progress
export const saveLearnModeProgress = (progress) => {
  return saveOfflineData(STORAGE_KEYS.LEARN_MODE_PROGRESS, progress);
};

// Get Learn Mode progress
export const getLearnModeProgress = () => {
  const stored = getOfflineData(STORAGE_KEYS.LEARN_MODE_PROGRESS);
  return stored?.data || null;
};

// Add item to pending sync queue
export const addToPendingSync = (action) => {
  const stored = getOfflineData(STORAGE_KEYS.PENDING_SYNC);
  const pendingActions = stored?.data || [];
  pendingActions.push({
    ...action,
    timestamp: Date.now(),
    id: `${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
  });
  return saveOfflineData(STORAGE_KEYS.PENDING_SYNC, pendingActions);
};

// Get pending sync items
export const getPendingSync = () => {
  const stored = getOfflineData(STORAGE_KEYS.PENDING_SYNC);
  return stored?.data || [];
};

// Clear pending sync items
export const clearPendingSync = () => {
  return saveOfflineData(STORAGE_KEYS.PENDING_SYNC, []);
};

// Sync pending items when online
export const syncPendingItems = async (api) => {
  if (!navigator.onLine) return { success: false, message: 'Still offline' };
  
  const pendingItems = getPendingSync();
  if (pendingItems.length === 0) return { success: true, message: 'Nothing to sync' };
  
  const results = [];
  
  for (const item of pendingItems) {
    try {
      if (item.type === 'news_reactor_level') {
        await api.post('/news-reactor/complete-level', {
          level: item.level,
          returnPct: item.returnPct
        });
        results.push({ id: item.id, success: true });
      }
    } catch (error) {
      results.push({ id: item.id, success: false, error: error.message });
    }
  }
  
  // Clear successfully synced items
  const failedItems = pendingItems.filter((item, index) => !results[index]?.success);
  saveOfflineData(STORAGE_KEYS.PENDING_SYNC, failedItems);
  
  return {
    success: failedItems.length === 0,
    synced: results.filter(r => r.success).length,
    failed: failedItems.length
  };
};

// Check if we're online
export const isOnline = () => navigator.onLine;

// Cache user data for offline access
export const cacheUserData = (userData) => {
  return saveOfflineData(STORAGE_KEYS.USER_DATA, userData);
};

// Get cached user data
export const getCachedUserData = () => {
  const stored = getOfflineData(STORAGE_KEYS.USER_DATA);
  return stored?.data || null;
};

export default {
  saveOfflineData,
  getOfflineData,
  saveNewsReactorProgress,
  getNewsReactorProgress,
  saveLearnModeProgress,
  getLearnModeProgress,
  addToPendingSync,
  getPendingSync,
  clearPendingSync,
  syncPendingItems,
  isOnline,
  cacheUserData,
  getCachedUserData,
  STORAGE_KEYS
};
